import React, { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Car,
  MapPin,
  BarChart3,
  PieChart,
  Download,
  Filter,
  Calendar,
  RefreshCw,
  Users,
  Activity,
  Target,
  Settings
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { usePricings, useAllPricings, useCities, useVehicleCategories } from '@/api-services/hooks/usePricing';
import { BasePricing, CityMaster, VehicleCategory } from '@/api-services/hooks/pricingTypes';

const PricingAnalyticsPage: React.FC = () => {
  const [selectedCity, setSelectedCity] = useState<number | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<number | null>(null);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year'>('month');
  const [refreshKey, setRefreshKey] = useState(0);

  // Fetch all data for analytics
  const { data: allPricings, refetch: refetchPricings } = useAllPricings({});

  const { data: cities } = useCities({});

  const { data: vehicleCategories } = useVehicleCategories({});

  // Analytics calculations
  const analytics = useMemo(() => {
    if (!allPricings?.length) {
      return {
        totalPricings: 0,
        activePricings: 0,
        inactivePricings: 0,
        averageBaseFare: 0,
        averageRatePerKm: 0,
        averageRatePerMinute: 0,
        averageMinimumFare: 0,
        averageNightCharges: 0,
        totalCities: 0,
        totalVehicleCategories: 0,
        pricingByCity: [],
        pricingByVehicle: [],
        distribution: [],
        trends: []
      };
    }

    const filteredPricings = allPricings.filter(pricing => {
      const cityMatch = selectedCity ? pricing.city?.cityId === selectedCity : true;
      const vehicleMatch = selectedVehicle ? pricing.vehicleCategoryId === selectedVehicle : true;
      return cityMatch && vehicleMatch;
    });

    const activePricings = filteredPricings.filter(p => p.isActive);
    const inactivePricings = filteredPricings.filter(p => !p.isActive);

    const averageBaseFare = filteredPricings.reduce((sum, p) => sum + p.baseFare, 0) / filteredPricings.length || 0;
    const averageRatePerKm = filteredPricings.reduce((sum, p) => sum + p.ratePerKm, 0) / filteredPricings.length || 0;
    const averageRatePerMinute = filteredPricings.reduce((sum, p) => sum + p.ratePerMinute, 0) / filteredPricings.length || 0;
    const averageMinimumFare = filteredPricings.reduce((sum, p) => sum + p.minimumFare, 0) / filteredPricings.length || 0;
    const averageNightCharges = filteredPricings.reduce((sum, p) => sum + p.nightCharges, 0) / filteredPricings.length || 0;

    // Group by city
    const pricingByCity = cities?.map(city => {
      const cityPricings = filteredPricings.filter(p => p.city?.cityId === city.cityId);
      return {
        name: city.cityName,
        count: cityPricings.length,
        activeCount: cityPricings.filter(p => p.isActive).length,
        averageFare: cityPricings.reduce((sum, p) => sum + p.baseFare, 0) / cityPricings.length || 0
      };
    }).filter(city => city.count > 0) || [];

    // Group by vehicle category
    const pricingByVehicle = vehicleCategories?.map(vehicle => {
      const vehiclePricings = filteredPricings.filter(p => p.vehicleCategoryId === vehicle.vehicleCategoryId);
      return {
        name: vehicle.vehicleCategoryName,
        maxPassengers: vehicle.maxPassengerCount,
        count: vehiclePricings.length,
        activeCount: vehiclePricings.filter(p => p.isActive).length,
        averageFare: vehiclePricings.reduce((sum, p) => sum + p.baseFare, 0) / vehiclePricings.length || 0
      };
    }).filter(vehicle => vehicle.count > 0) || [];

    // Status distribution
    const distribution = [
      { name: 'Active', value: activePricings.length, color: '#10B981' },
      { name: 'Inactive', value: inactivePricings.length, color: '#EF4444' }
    ];

    // Generate trend data (mock data for demo)
    const generateTrendData = () => {
      const now = new Date();
      const days = timeRange === 'week' ? 7 : timeRange === 'month' ? 30 : 365;
      const data = [];

      for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);
        data.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          pricings: Math.floor(Math.random() * 20) + 10,
          activePricings: Math.floor(Math.random() * 15) + 5
        });
      }
      return data;
    };

    return {
      totalPricings: filteredPricings.length,
      activePricings: activePricings.length,
      inactivePricings: inactivePricings.length,
      averageBaseFare,
      averageRatePerKm,
      averageRatePerMinute,
      averageMinimumFare,
      averageNightCharges,
      totalCities: cities?.length || 0,
      totalVehicleCategories: vehicleCategories?.length || 0,
      pricingByCity,
      pricingByVehicle,
      distribution,
      trends: generateTrendData()
    };
  }, [allPricings, selectedCity, selectedVehicle, cities, vehicleCategories, timeRange]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    refetchPricings();
  };

  const handleExport = () => {
    const csvContent = [
      ['City', 'Vehicle Category', 'Base Fare', 'Rate/KM', 'Rate/Min', 'Minimum Fare', 'Night Charges', 'Status'],
      ...allPricings?.map(pricing => [
        pricing.city?.cityName || 'N/A',
        getVehicleCategoryName(pricing.vehicleCategoryId),
        pricing.baseFare.toFixed(2),
        pricing.ratePerKm.toFixed(2),
        pricing.ratePerMinute.toFixed(2),
        pricing.minimumFare.toFixed(2),
        pricing.nightCharges.toFixed(2),
        pricing.isActive ? 'Active' : 'Inactive'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pricing-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Analytics data exported successfully!');
  };

  const getVehicleCategoryName = useCallback((vehicleCategoryId: number): string => {
    const category = vehicleCategories?.find(vc => vc.vehicleCategoryId === vehicleCategoryId);
    return category?.vehicleCategoryName || `Vehicle ${vehicleCategoryId}`;
  }, [vehicleCategories]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Pricing Analytics</h1>
          <p className="text-muted-foreground">
            Comprehensive insights and analytics for your pricing strategy
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={selectedCity?.toString() || ''} onValueChange={(value) => setSelectedCity(value ? parseInt(value) : null)}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All Cities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Cities</SelectItem>
                {cities?.map((city) => (
                  <SelectItem key={city.cityId} value={city.cityId.toString()}>
                    {city.cityName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedVehicle?.toString() || ''} onValueChange={(value) => setSelectedVehicle(value ? parseInt(value) : null)}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All Vehicles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Vehicles</SelectItem>
                {vehicleCategories?.map((vehicle) => (
                  <SelectItem key={vehicle.vehicleCategoryId} value={vehicle.vehicleCategoryId.toString()}>
                    {vehicle.vehicleCategoryName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={timeRange} onValueChange={(value: 'week' | 'month' | 'year') => setTimeRange(value)}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Last Week</SelectItem>
                <SelectItem value="month">Last Month</SelectItem>
                <SelectItem value="year">Last Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Pricings</p>
                <p className="text-2xl font-bold">{analytics.totalPricings}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Pricings</p>
                <p className="text-2xl font-bold text-green-600">{analytics.activePricings}</p>
              </div>
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Average Base Fare</p>
                <p className="text-2xl font-bold">${analytics.averageBaseFare.toFixed(2)}</p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Coverage</p>
                <p className="text-2xl font-bold">
                  {analytics.totalCities} cities, {analytics.totalVehicleCategories} vehicles
                </p>
              </div>
              <div className="p-2 bg-orange-100 rounded-lg">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5" />
              Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={analytics.distribution}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {analytics.distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pricing Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Pricing Trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics.trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="pricings" stroke="#8884d8" name="Total Pricings" />
                  <Line type="monotone" dataKey="activePricings" stroke="#10B981" name="Active Pricings" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pricing by City */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Pricing by City
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.pricingByCity.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No data available</p>
              ) : (
                analytics.pricingByCity.map((city, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{city.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {city.activeCount} of {city.count} active
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${city.averageFare.toFixed(2)}</p>
                      <p className="text-sm text-muted-foreground">avg fare</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pricing by Vehicle */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Car className="w-5 h-5" />
              Pricing by Vehicle Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.pricingByVehicle.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No data available</p>
              ) : (
                analytics.pricingByVehicle.map((vehicle, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{vehicle.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {vehicle.maxPassengers} passengers • {vehicle.activeCount} of {vehicle.count} active
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${vehicle.averageFare.toFixed(2)}</p>
                      <p className="text-sm text-muted-foreground">avg fare</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Stats */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Detailed Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-4 border rounded-lg">
              <p className="text-sm text-muted-foreground">Avg Rate/KM</p>
              <p className="text-2xl font-bold text-blue-600">${analytics.averageRatePerKm.toFixed(2)}</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <p className="text-sm text-muted-foreground">Avg Rate/Minute</p>
              <p className="text-2xl font-bold text-orange-600">${analytics.averageRatePerMinute.toFixed(2)}</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <p className="text-sm text-muted-foreground">Avg Minimum Fare</p>
              <p className="text-2xl font-bold text-purple-600">${analytics.averageMinimumFare.toFixed(2)}</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <p className="text-sm text-muted-foreground">Avg Night Charges</p>
              <p className="text-2xl font-bold text-indigo-600">${analytics.averageNightCharges.toFixed(2)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PricingAnalyticsPage;